import React, { useEffect, useRef, useState } from 'react'
import { Dices } from 'lucide-react'
import { CATEGORIES, GAMES, PLAYABLE_IDS, accent, categoryText, gameText } from '../games/catalog.js'
import { tr, useLang } from '../games/lib/lang.js'
import { prefetchAjedrez } from '../games/ajedrez/prefetch.js'

const ROTATING = PLAYABLE_IDS.filter((id) => id !== 'ajedrez')

function cardArt(id) {
  return id === 'ajedrez' ? '/ajedrez-portada.webp' : `/cards/${id}.webp`
}

function categoryStyle(hex) {
  return {
    background: `radial-gradient(120% 95% at 18% 0%, ${hex}26 0%, transparent 55%), linear-gradient(160deg, #101728 0%, #0a0f1c 100%)`,
  }
}

function pickGames(count) {
  const bag = [...ROTATING]
  const out = []
  while (out.length < count && bag.length) {
    out.push(bag.splice(Math.floor(Math.random() * bag.length), 1)[0])
  }
  return out
}

function useRotatingGames(count) {
  const [ids, setIds] = useState(() => pickGames(count))
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    const timer = setInterval(() => {
      setIds(pickGames(count))
      setTick((n) => n + 1)
    }, 4200)
    return () => clearInterval(timer)
  }, [count])
  return { ids, tick }
}

const CHESS_ART = ['/chess/card-duel.webp', '/chess/card-arena.webp', '/chess/card-macro.webp']

function ChessCover({ onPlay, lang }) {
  const [art, setArt] = useState(0)
  useEffect(() => {
    prefetchAjedrez()
  }, [])
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    const timer = setInterval(() => setArt((n) => (n + 1) % CHESS_ART.length), 4200)
    return () => clearInterval(timer)
  }, [])
  return (
    <button
      type="button"
      className="cbp-chess"
      onMouseEnter={() => prefetchAjedrez()}
      onFocus={() => prefetchAjedrez()}
      onClick={() => onPlay('ajedrez')}
    >
      {CHESS_ART.map((src, i) => (
        <img
          key={src}
          className={`cbp-chess__art${i === art ? ' is-on' : ''}`}
          src={src}
          alt=""
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding="async"
        />
      ))}
      <span className="cbp-chess__veil" aria-hidden="true" />
      <span className="cbp-chess__copy">
        <p className="cbp-chess__kicker">{tr(lang, 'Coliseo cósmico', 'Cosmic coliseum')}</p>
        <h2>{tr(lang, 'Ajedrez', 'Chess')}</h2>
        <p>{tr(lang, 'Cuatro ejércitos. Una partida que se acaba en mate.', 'Four armies. A game that ends in checkmate.')}</p>
        <span className="cbp-chess__cta">{tr(lang, 'Jugar ahora', 'Play now')}</span>
      </span>
    </button>
  )
}

function RotatingTile({ gameId, tick, onLaunch, lang }) {
  const g = gameText(GAMES[gameId], lang)
  const a = accent(g.accent)
  return (
    <button
      type="button"
      key={`${gameId}-${tick}`}
      className="cbp-tile cbp-swap"
      onClick={() => onLaunch(g.gameId)}
    >
      <img className="cbp-tile__art" src={cardArt(gameId)} alt="" loading="lazy" decoding="async" />
      <span className="cbp-tile__copy">
        <span className="text-sm font-black text-white">{g.name}</span>
        <span className={`text-[11px] ${a.text}`}>{g.trains}</span>
      </span>
    </button>
  )
}

function CategorySlide({ onLaunch, lang }) {
  const track = useRef(null)
  const [index, setIndex] = useState(0)

  const go = (dir) => {
    const next = (index + dir + CATEGORIES.length) % CATEGORIES.length
    setIndex(next)
    track.current?.children[next]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
  }

  return (
    <section className="cbp-cats" aria-label={tr(lang, 'Categorías', 'Categories')}>
      <button type="button" className="cbp-cats__nav cbp-cats__nav--prev" aria-label={tr(lang, 'Categoría anterior', 'Previous category')} onClick={() => go(-1)}>‹</button>
      <button type="button" className="cbp-cats__nav cbp-cats__nav--next" aria-label={tr(lang, 'Categoría siguiente', 'Next category')} onClick={() => go(1)}>›</button>
      <div
        className="cbp-cats__track"
        ref={track}
        onScroll={(event) => {
          const el = event.currentTarget
          const page = Math.round(el.scrollLeft / Math.max(el.clientWidth, 1))
          if (page !== index) setIndex(page)
        }}
      >
        {CATEGORIES.map((raw) => {
          const cat = categoryText(raw, lang)
          const a = accent(cat.accent)
          return (
            <article key={cat.id} className="cbp-cats__page" style={categoryStyle(a.hex)}>
              <header className="flex items-center gap-2 pr-16">
                <span className="text-2xl" aria-hidden="true">{cat.emoji}</span>
                <div>
                  <h2 className="text-base font-black text-white">{cat.label}</h2>
                  <p className="text-[12px] text-slate-300">{cat.blurb}</p>
                </div>
              </header>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cat.items.map((gid) => {
                  const g = gameText(GAMES[gid], lang)
                  return (
                    <button
                      key={gid}
                      type="button"
                      onClick={() => onLaunch(g.gameId)}
                      className="cbp-mini"
                    >
                      <img className="cbp-mini__art" src={cardArt(gid)} alt="" loading="lazy" decoding="async" />
                      <span className="cbp-mini__copy">
                        <span className="block text-[12px] font-bold text-white">{g.name}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

function RandomModal({ open, onClose, onLaunch, bias, lang }) {
  const [rolling, setRolling] = useState(false)
  const [shown, setShown] = useState(PLAYABLE_IDS[0])
  const [picked, setPicked] = useState(false)
  const timers = useRef([])

  useEffect(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (!open) return
    setRolling(true)
    setPicked(false)
    const total = 20
    for (let n = 0; n < total; n++) {
      timers.current.push(
        setTimeout(() => setShown(PLAYABLE_IDS[(n * 5 + 3) % PLAYABLE_IDS.length]), n * 70),
      )
    }
    timers.current.push(
      setTimeout(() => {
        setShown(PLAYABLE_IDS[Math.floor(Math.random() * PLAYABLE_IDS.length)])
        setPicked(true)
        setRolling(false)
      }, total * 70 + 60),
    )
    return () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    }
  }, [open, bias])

  if (!open) return null
  const g = gameText(GAMES[shown], lang)
  const a = accent(g.accent)

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-xs rounded-[28px] border border-slate-800 bg-[#0b101d]/95 cbp-card p-6 text-center"
        onClick={(e) => e.stopPropagation()}
        style={categoryStyle(a.hex)}
      >
        <div
          className="w-28 h-28 mx-auto rounded-3xl overflow-hidden border border-white/10"
          style={{ boxShadow: `0 0 40px ${a.hex}33`, transform: rolling ? 'scale(.94)' : 'scale(1)' }}
        >
          <img src={cardArt(shown)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <h2 className={`text-xl font-black mt-4 ${rolling ? 'text-slate-400' : 'text-white'}`}>{g.name}</h2>

        <button
          type="button"
          disabled={rolling || !picked}
          onClick={() => onLaunch(g.gameId)}
          className={`w-full mt-5 py-3 rounded-xl font-black text-sm text-slate-950 bg-gradient-to-r ${a.grad} disabled:opacity-40 cursor-pointer`}
        >
          {rolling ? tr(lang, 'Girando…', 'Spinning…') : tr(lang, 'Jugar', 'Play')}
        </button>
        <button
          type="button"
          disabled={rolling}
          onClick={onClose}
          className="w-full mt-2 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 text-xs font-bold disabled:opacity-40 cursor-pointer"
        >
          {tr(lang, 'Cerrar', 'Close')}
        </button>
      </div>
    </div>
  )
}

export function GameHub({ onSelectModule }) {
  const [lang] = useLang()
  const [randomOpen, setRandomOpen] = useState(false)
  const [bias, setBias] = useState(0)
  const rotating = useRotatingGames(4)

  useEffect(() => {
    const run = () => prefetchAjedrez()
    const idle = window.requestIdleCallback?.(run, { timeout: 2200 })
    const fallback = idle ? null : window.setTimeout(run, 900)
    return () => {
      if (idle) window.cancelIdleCallback?.(idle)
      if (fallback) window.clearTimeout(fallback)
    }
  }, [])

  const launch = (gameId) => {
    setRandomOpen(false)
    onSelectModule?.(gameId)
  }

  return (
    <div className="relative">
      <div className="cbp-starfield" />

      <div className="relative z-10 cbp-stage">
        <ChessCover onPlay={launch} lang={lang} />
        {rotating.ids.map((id) => (
          <RotatingTile key={id} gameId={id} tick={rotating.tick} onLaunch={launch} lang={lang} />
        ))}
      </div>
      <CategorySlide onLaunch={launch} lang={lang} />

      {/* Floating random button */}
      <button
        type="button"
        onClick={() => {
          setBias((b) => b + 1)
          setRandomOpen(true)
        }}
        aria-label={tr(lang, 'Jugar al azar', 'Play at random')}
        className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-30 w-16 h-16 rounded-full flex items-center justify-center text-slate-950 shadow-2xl shadow-cyan-500/30 active:scale-95 transition-transform"
        style={{ background: 'linear-gradient(180deg,#ffc857,#f59e0b)' }}
      >
        <span className="absolute inset-0 rounded-full border border-white/40 animate-ping opacity-40" />
        <Dices size={26} />
      </button>

      <RandomModal open={randomOpen} onClose={() => setRandomOpen(false)} onLaunch={launch} bias={bias} lang={lang} />
    </div>
  )
}
